import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChangeApprovalsApi } from 'types/backend';
import { changeApprovals } from 'services/api';
import {
  EnvironmentApprovalSettingsResponse,
  UpdateEnvironmentApprovalSettingsRequest,
} from 'types/backend';
import { APPROVAL_SETTINGS_QUERY_KEY } from './useApprovalSettingsData';

export const ENVIRONMENT_APPROVAL_SETTINGS_QUERY_KEY: string[] = [
  'environment-approval-settings',
];

export interface EnvironmentApprovalSettingsDataProps {
  environmentApprovalSettings: EnvironmentApprovalSettingsResponse | undefined;
  isFetchingEnvironmentApprovalSettings: boolean;
  isLoadingEnvironmentApprovalSettings: boolean;
  refetchEnvironmentApprovalSettings?: () => Promise<
    EnvironmentApprovalSettingsResponse | undefined
  >;
  updateEnvironmentApprovalSettings: (
    args: UpdateEnvironmentApprovalSettingsRequest
  ) => Promise<EnvironmentApprovalSettingsResponse | undefined>;
}

/**
 * Service to fetch environment approval settings.
 */
export const getEnvironmentApprovalSettingsQueryService = ({
  changeApprovals,
  environmentId,
  projectId,
}: {
  changeApprovals: ChangeApprovalsApi;
  environmentId: number | null;
  projectId: number;
}) => ({
  fetch: async (): Promise<EnvironmentApprovalSettingsResponse> => {
    try {
      if (!environmentId) {
        throw new Error(
          `Failed to fetch approval settings. Environment ID is not defined.`
        );
      }
      return await changeApprovals.getEnvironmentApprovalSettings({
        environment_id: environmentId,
        project_id: projectId,
      });
    } catch (error) {
      throw new Error(
        `Failed to fetch approval settings for Project ID: ${projectId} and Environment ID: ${environmentId}`
      );
    }
  },
});

export const getEnvironmentApprovalSettingsMutationService = ({
  changeApprovals,
  environmentId,
  projectId,
}: {
  changeApprovals: ChangeApprovalsApi;
  environmentId: number | null;
  projectId: number;
}) => ({
  update: async (requestParams: UpdateEnvironmentApprovalSettingsRequest) => {
    try {
      return await changeApprovals.updateEnvironmentApprovalSettings(
        requestParams
      );
    } catch (error) {
      throw Object.assign(error as {}, {
        message: `Updating approval settings for project ${projectId} and environment ${environmentId} failed.`,
      });
    }
  },
});

/**
 * Custom hook to fetch and create approval settings for an environment.
 *
 * @param projectId - The project ID
 * @param environmentId - The environment ID
 * @returns {EnvironmentApprovalSettingsDataProps}
 */
const useEnvironmentApprovalSettingsData = (
  projectId: number,
  environmentId: number
): EnvironmentApprovalSettingsDataProps => {
  const queryService = getEnvironmentApprovalSettingsQueryService({
    changeApprovals,
    environmentId,
    projectId,
  });

  const environmentApprovalSettingsQueryKey = [
    ...ENVIRONMENT_APPROVAL_SETTINGS_QUERY_KEY,
    { environmentId, projectId },
  ];

  const queryClient = useQueryClient();

  const {
    data: environmentApprovalSettings,
    isLoading: isLoadingEnvironmentApprovalSettings,
    isFetching: isFetchingEnvironmentApprovalSettings,
    refetch: refetchEnvironmentApprovalSettings,
  } = useQuery({
    queryFn: queryService.fetch,
    queryKey: environmentApprovalSettingsQueryKey,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const mutationService = useMemo(
    () =>
      getEnvironmentApprovalSettingsMutationService({
        changeApprovals,
        environmentId,
        projectId,
      }),
    [projectId, environmentId]
  );

  const { mutateAsync: updateEnvironmentApprovalSettings } = useMutation({
    mutationFn: mutationService.update,
    onError: error => {
      throw new Error(
        JSON.stringify({
          message: `Updating approval settings for project ${projectId} and environment ${environmentId} failed.`,
          ...(error as {}),
        })
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: APPROVAL_SETTINGS_QUERY_KEY,
      });
      await queryClient.invalidateQueries({
        queryKey: ENVIRONMENT_APPROVAL_SETTINGS_QUERY_KEY,
      });
      refetchEnvironmentApprovalSettings();
    },
  });

  return {
    environmentApprovalSettings,
    isFetchingEnvironmentApprovalSettings,
    isLoadingEnvironmentApprovalSettings,
    refetchEnvironmentApprovalSettings,
    updateEnvironmentApprovalSettings,
  };
};

export default useEnvironmentApprovalSettingsData;
